/**
 * When the storefront's email signup popup may open.
 *
 * What a visitor did with it is kept in this browser only. Like the pasang
 * notice it is a convenience, not a record: if storage is blocked or wiped the
 * popup simply shows again, and the server holds the actual subscription.
 */

const KEY = "kanata:signup-popup";

/** How long a closed popup stays away before it asks again. */
export const DISMISS_FOR_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * What this browser did with the popup. Subscribing and closing are kept
 * apart, because they suppress different popups: a signup only hides a popup
 * that asks for an email again, and a close only hides the popup that was
 * closed — a new campaign, with a different key, shows again.
 *
 * Also reads the shape this replaced, {state, at}, which carried no key and
 * so matches any popup.
 */
interface Remembered {
  subscribed?: boolean;
  dismissedAt?: number;
  dismissedKey?: string;
}

/** The popup as far as memory is concerned. */
export interface PopupIdentity {
  /** From popupKey: changes when the campaign does. */
  key: string;
  collectEmail: boolean;
}

/**
 * Identifies a campaign: its title, whether it asks for an email, and where it
 * links. Editing the copy's punctuation re-shows it, which is the safe side.
 */
export function popupKey(p: { title: string; collectEmail: boolean; ctaHref?: string }): string {
  return [p.title, p.collectEmail ? "email" : "", p.ctaHref ?? ""].join("|");
}

/**
 * Pages the popup never opens over: someone in the cart, at checkout or on
 * the payment page is already buying, and an interruption there costs orders.
 */
const HIDDEN_PREFIXES = ["/keranjang", "/checkout", "/bayar"];

export function popupAllowedOn(pathname: string): boolean {
  return !HIDDEN_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/**
 * Whether this browser has already dealt with this popup: subscribed, when it
 * asks for an email, or closed this same campaign within 30 days.
 */
export function popupSuppressed(
  popup: PopupIdentity,
  storage: Pick<Storage, "getItem"> | undefined = globalStorage(),
  now: number = Date.now(),
): boolean {
  const r = read(storage);
  if (r.subscribed && popup.collectEmail) return true;
  if (r.dismissedAt === undefined || now - r.dismissedAt >= DISMISS_FOR_MS) return false;
  return r.dismissedKey === undefined || r.dismissedKey === popup.key;
}

/** Closing it after signing up keeps the signup remembered. */
export function rememberPopupDismissed(
  key: string,
  storage: Pick<Storage, "getItem" | "setItem"> | undefined = globalStorage(),
  now: number = Date.now(),
): void {
  write(storage, { ...read(storage), dismissedAt: now, dismissedKey: key });
}

export function rememberPopupSubscribed(
  storage: Pick<Storage, "getItem" | "setItem"> | undefined = globalStorage(),
): void {
  write(storage, { ...read(storage), subscribed: true });
}

function read(storage: Pick<Storage, "getItem"> | undefined): Remembered {
  try {
    const raw = storage?.getItem(KEY);
    if (!raw) return {};
    const r = JSON.parse(raw) as Remembered & { state?: string; at?: number };
    if (r.state === "subscribed") return { subscribed: true };
    if (r.state === "dismissed") return typeof r.at === "number" ? { dismissedAt: r.at } : {};
    return {
      subscribed: r.subscribed === true || undefined,
      dismissedAt: typeof r.dismissedAt === "number" ? r.dismissedAt : undefined,
      dismissedKey: typeof r.dismissedKey === "string" ? r.dismissedKey : undefined,
    };
  } catch {
    // Unreadable is as good as nothing remembered.
    return {};
  }
}

function write(storage: Pick<Storage, "setItem"> | undefined, r: Remembered) {
  try {
    storage?.setItem(KEY, JSON.stringify(r));
  } catch {
    // Nowhere to remember it; the popup shows next visit.
  }
}

function globalStorage(): Storage | undefined {
  try {
    return typeof window === "undefined" ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}
